import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Gift, AlertCircle, X } from 'lucide-react';

const API = `${window.location.origin}/api`;
const DISMISS_KEY = 'jsf_prize_reminder_dismissed';

/**
 * Top-Seller monthly prize reminder.
 *
 * Fires whenever the signed-in ambassador has an unclaimed prize whose
 * 7-day claim window is still open. We show this on the Ambassador
 * Dashboard (and could be extended site-wide if needed) — so every login
 * counts down the deadline:
 *   • Day 7-3: gold, friendly nudge
 *   • Day 2-1: orange, more urgent
 *   • Day 0 (last day): red, "claim today"
 *
 * Users can dismiss for the current login session (sessionStorage) but
 * the banner returns on next login until they actually claim or the
 * window expires. This is intentional — losing a prize to inaction
 * would be a worse UX than mild banner annoyance.
 */
export default function PrizeClaimReminder() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      // Per-prize dismissal so a NEW month's prize re-shows even if the
      // user dismissed a previous one this session.
      const raw = sessionStorage.getItem(DISMISS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      // We can't compare to the prize yet (haven't fetched) — handled below.
      // Just hold onto the dismissed set for now.
      if (parsed) setDismissed(parsed);
    } catch { /* sessionStorage blocked */ }

    const token = localStorage.getItem('auth_token');
    if (!token) return;
    axios.get(`${API}/ambassador/me/active-prize-reminder`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setData(r.data?.reminder || null))
      .catch(() => setData(null));
  }, []);

  if (!data) return null;
  const key = `${data.year}-${data.month}`;
  const isDismissed = dismissed && typeof dismissed === 'object' && dismissed[key];
  if (isDismissed) return null;

  const dismiss = () => {
    const next = { ...(typeof dismissed === 'object' ? dismissed : {}), [key]: true };
    setDismissed(next);
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch { /* blocked */ }
  };

  const urgent = data.days_left <= 1;
  const warning = data.days_left <= 3 && data.days_left > 1;
  const tone = urgent
    ? { bg: 'bg-red-500/10', border: 'border-red-400', text: 'text-red-300', icon: 'text-red-400', accent: 'text-red-400' }
    : warning
      ? { bg: 'bg-[#F59E0B]/10', border: 'border-[#F59E0B]', text: 'text-[#F59E0B]', icon: 'text-[#F59E0B]', accent: 'text-[#F59E0B]' }
      : { bg: 'bg-[#D4AF37]/10', border: 'border-[#D4AF37]', text: 'text-[#D4AF37]', icon: 'text-[#D4AF37]', accent: 'text-[#D4AF37]' };

  return (
    <div
      data-testid="prize-claim-reminder"
      className={`mb-6 ${tone.bg} border-l-4 ${tone.border} px-5 py-4 flex items-start gap-3`}
    >
      {urgent ? (
        <AlertCircle className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} strokeWidth={2} />
      ) : (
        <Gift className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} strokeWidth={2} />
      )}
      <div className="flex-1 min-w-0">
        <p className={`${tone.text} text-sm font-sans font-semibold mb-1`}>
          {urgent
            ? data.days_left === 0
              ? `⏰ Last day! Claim your ${data.month_label} Top Seller prize TODAY`
              : `Only 1 day left — claim your ${data.month_label} prize`
            : `You have ${data.days_left} days to claim your ${data.month_label} Top Seller prize`}
        </p>
        <p className="text-white/60 text-xs font-sans">
          You sold <span className="text-white">{data.bottles} bottles</span> for{' '}
          <span className="text-white">₱{Number(data.revenue).toLocaleString()}</span>.
          Prize: cash bonus + free 50ml bottle + spotlight + official certificate.
        </p>
        <Link
          to={`/certificate/${data.year}/${data.month}`}
          data-testid="prize-claim-reminder-cta"
          className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1C7D3F] text-white text-xs uppercase tracking-[0.2em] font-sans hover:bg-[#2E9B50] transition-colors`}
        >
          <Gift className="w-3 h-3" /> Claim now
        </Link>
      </div>
      <button
        type="button"
        onClick={dismiss}
        title="Hide until next login"
        className="text-white/40 hover:text-white p-1"
        data-testid="prize-claim-reminder-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
